import EntityAttachments from 'components/EntityAttachments';
import FormRoleSelector from 'components/Form/FormRoleSelector';
import HmsEntityFormController from 'components/Form/HmsEntityFormController';
import HmsEntityFormGridBasic from 'components/Form/HmsEntityFormGridBasic';
import HmsField from 'components/Form/HmsField';
import HmsTabbedTables from 'components/HmsTabbedTables';
import UserInvites from 'components/User/UserInvites';
import UserManagedTeams from 'components/User/UserManagedTeams';
import UserOrganizations from 'components/User/UserOrganizations';
import UserPlayers from 'components/User/UserPlayers';
import useEntities from 'hooks/useEntities';
import useLists from 'hooks/useLists';
import useLoadEntity from 'hooks/useLoadEntity';
import useOrganization from 'hooks/useOrganization';
import { useRef } from 'react';
import useLoginStore from 'store/loginStore';
import icons from 'utils/icons';

const User = () => {
  const entities = useEntities();
  const formControllerRef = useRef(null);

  const { organization } = useOrganization();
  const ORGANIZATION_ID = organization?.organizationId;

  const entity = entities.User;
  const { id, isLoading, isError, isNotFound, data, reload, relationsData, reloadRelations } = useLoadEntity({
    entity,
    relations: [
      'Organizations',
      'ManagedTeams',
      'Players',
      'Players>Organization',
    ].join(','),
  });
  const { relationsData: attachmentsData, reloadRelations: reloadAttachments } = useLoadEntity({
    entity,
    relations: [
      'Attachments',
    ].join(',')
  });  
  const { relationsData: invitesData, reloadRelations: reloadInvites } = useLoadEntity({
    entity,
    relations: [
      'Invites',
    ].join(','),
  });
  const { userRoles } = useLists();
  const isSystemAdmin = useLoginStore(s => s.isSystemAdmin)();

  // Filter out PLAYER role if season player registration is disabled
  const availableUserRoles = organization?.seasonPlayerRegistrationEnabled 
    ? userRoles 
    : userRoles.filter(role => role.value !== 'PLAYER');




  return (
    <>
      <HmsEntityFormController
        ref={formControllerRef}
        {... { entity, id, isLoading, isError, isNotFound, data, reload }}
        header={({ data }) => {
          if (id == '_new_') return null;
          const rolesValue = data?.roles;
          const rolesArray = Array.isArray(rolesValue)
            ? rolesValue
            : (typeof rolesValue === 'string' && rolesValue.length > 0 ? rolesValue.split(',').map(r => r.trim()) : []);
          const hasRole = (role) => rolesArray.includes(role);

          const linkedPlayersCount = relationsData?.Players?.length ?? 0;
          const managedTeamsCount = relationsData?.ManagedTeams?.length ?? 0;
          const organizationsCount = relationsData?.Organizations?.length ?? 0;

          const warnings = [];
          if (organizationsCount === 0) {
            warnings.push('User is not linked to any organization.');
          }
          if (hasRole('PLAYER') && linkedPlayersCount === 0) {
            warnings.push('Player role is selected but no linked player exists.');
          }
          if (!hasRole('PLAYER') && linkedPlayersCount > 0) {
            warnings.push('A linked player exists but the Player role is not selected.');
          }
          if (hasRole('TEAM_MANAGER') && managedTeamsCount === 0) {
            warnings.push('Team Manager role is selected but no managed team is linked.');
          }
          if (!hasRole('TEAM_MANAGER') && managedTeamsCount > 0) {
            warnings.push('A managed team is linked but the Team Manager role is not selected.');
          }

          return warnings.length > 0 ? (
            <div className="m-2 border-[1px] border-orange-500 p-3 bg-orange-100 flex flex-col gap-2">
              <div className="flex items-center gap-2 text-orange-600 font-bold">
                {icons.ErrorTriangleOutlined}
                <span>WARNING</span>
              </div>
              <div className="text-orange-700">
                {warnings.map((w, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <span className="mt-[2px] text-orange-500">•</span>
                    <span>{w}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null;
        }}
        beforeSave={(data) => {
          // set organizationId to user's current organization (if user is not system admin 
          // - system admin can see all users even those outside his orgs) but org admin can not
          if (!isSystemAdmin) data.Organizations = ORGANIZATION_ID; //opeq     
          return data;
        }}
        afterSave={async () => {
          reload();
        }}
        form={(
          <>
            <HmsEntityFormGridBasic>
              <HmsField name="firstName" autoFocus />
              <HmsField name="lastName" />
              <FormRoleSelector 
                name={'roles'} 
                label={'Roles'} 
                formItemProps={{ required: true }} 
                options={availableUserRoles}
                dataType="CSV"
              />
            </HmsEntityFormGridBasic>
            <HmsEntityFormGridBasic>
              <HmsField name="email" />
              <HmsField name="password" password={true} fieldItemProps={{ editable: id == '_new_' }} />
              <HmsField name="phone" />
            </HmsEntityFormGridBasic>
          </>
        )}
        footer={id != '_new_' && (
          <div className="mt-4">
            <div className="mt-4">
              <HmsTabbedTables
                tables={[
                  {
                    title: "Organizations" + (relationsData?.Organizations ? (' (' + relationsData?.Organizations?.length + ')') : ''),
                    children: <UserOrganizations
                      sourceEntityId={data?.userId}
                      data={relationsData?.Organizations}
                      onChange={reloadRelations}
                    />
                  },
                  {
                    title: "Managed Teams" + (relationsData?.ManagedTeams ? (' (' + relationsData?.ManagedTeams?.length + ')') : ''),
                    children: <UserManagedTeams
                      sourceEntityId={data?.userId}
                      data={relationsData?.ManagedTeams}
                      onChange={reloadRelations}
                    />
                  },
                  {
                    title: "Linked Players" + (relationsData?.Players ? (' (' + relationsData?.Players?.length + ')') : ''),
                    children: <UserPlayers
                      sourceEntityId={data?.userId}
                      data={relationsData?.Players}
                      onChange={reloadRelations}
                    />
                  },
                  {
                    title: "Invites" + (invitesData?.Invites ? (' (' + invitesData?.Invites?.length + ')') : ''),
                    children: <UserInvites 
                      sourceEntityId={data?.userId}
                      data={invitesData?.Invites}
                      onChange={reloadInvites}
                      sendInviteDisabledReason={
                        relationsData?.Organizations?.length == 0 ? 'User must be a member of at least one organization to send invites' :
                        null
                      }
                    />,
                  },
                  {
                    title: "Attachments" + (attachmentsData?.Attachments ? (' (' + attachmentsData?.Attachments?.length + ')') : ''),
                    children: <EntityAttachments entity={entity.name} id={data?.[entity.primaryKey]} data={attachmentsData} reload={reloadAttachments} />,
                  },
               ]}
              />
            </div>
          </div>
        )}
      />
    </>
  );
}

export default User
